import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { collection } from 'firebase/firestore';
import { db } from './firebase';
import { listenQuery } from './liveRead';
import { useAuth } from '../auth/AuthProvider';
import { canViewJobFinancials, type Job, type JobPricing } from '../types/models';

/**
 * Job prices and invoicing (1 Nov 2026). The server keeps them in
 * companies/{companyId}/jobFinance/{jobId}, which only Super Admin, Admin, Manager and
 * Accounts can read (firestore.rules); the job itself keeps only accountingStatus.
 * One listener here, for those roles only, holds the company's prices; pages merge a
 * job's price back in with withJobFinance so their price displays are unchanged.
 */
export interface JobFinance {
  pricing?: JobPricing | null;
  invoiceReference?: string | null;
  invoicedAt?: Job['invoicedAt'];
  invoicedBy?: string | null;
}

const EMPTY_FINANCE = new Map<string, JobFinance>();
interface FinanceState {
  scope: string | null;
  data: Map<string, JobFinance>;
  loading: boolean;
  error: string | null;
}
const JobFinanceContext = createContext({
  data: EMPTY_FINANCE, loading: true, error: null as string | null,
  ready: false, retry: () => {},
});

export function JobFinanceProvider({ children }: { children: ReactNode }) {
  const { activeCompanyId, activeRole, user } = useAuth();
  const allowed = canViewJobFinancials(activeRole ?? undefined);
  const scope = allowed && activeCompanyId && user ? JSON.stringify([user.uid, activeCompanyId, activeRole]) : null;
  const [attempt, setAttempt] = useState(0);
  const [finance, setFinance] = useState<FinanceState>({ scope: null, data: EMPTY_FINANCE, loading: true, error: null });

  useEffect(() => {
    setFinance({ scope, data: EMPTY_FINANCE, loading: !!scope, error: null });
    if (!activeCompanyId || !scope) return;
    return listenQuery(
      collection(db, 'companies', activeCompanyId, 'jobFinance'),
      (snap) => {
        const next = new Map<string, JobFinance>();
        snap.docs.forEach((d) => next.set(d.id, d.data() as JobFinance));
        setFinance({ scope, data: next, loading: false, error: null });
      },
      (error) => setFinance({ scope, data: EMPTY_FINANCE, loading: false, error: error.message })
    );
  }, [activeCompanyId, scope, attempt]);

  // A company/user/role change must hide the old records during render, before
  // the effect gets its chance to replace the listener.
  const current = scope && finance.scope === scope;
  const loading = !!scope && (!current || finance.loading);
  const error = current ? finance.error : null;
  const ready = !!scope && !loading && !error;
  return <JobFinanceContext.Provider value={{
    data: ready ? finance.data : EMPTY_FINANCE, loading, error, ready,
    retry: () => {
      setFinance({ scope, data: EMPTY_FINANCE, loading: !!scope, error: null });
      setAttempt((n) => n + 1);
    },
  }}>{children}</JobFinanceContext.Provider>;
}

export function useJobFinanceStatus() {
  const { loading, error, ready, retry } = useContext(JobFinanceContext);
  return { loading, error, ready, retry };
}

/** The job's price record, or undefined (none yet, or a role that can't see prices). */
export function useJobFinance(jobId: string | undefined): JobFinance | undefined {
  const { data: finance } = useContext(JobFinanceContext);
  return jobId ? finance.get(jobId) : undefined;
}

export function useJobFinanceMap(): Map<string, JobFinance> {
  return useContext(JobFinanceContext).data;
}

/** The job with its price record merged in; a price still on the job (before 1 Nov) is kept. */
export function withJobFinance<T extends Job>(job: T, finance: JobFinance | undefined): T {
  if (!finance) return job;
  return {
    ...job,
    pricing: finance.pricing ?? job.pricing ?? null,
    invoiceReference: finance.invoiceReference ?? job.invoiceReference ?? null,
    invoicedAt: finance.invoicedAt ?? job.invoicedAt ?? null,
    invoicedBy: finance.invoicedBy ?? job.invoicedBy ?? null,
  };
}
